/**
 * BiblioteekTab — Oefeningenbibliotheek voor bedrijfsaccounts
 * Sub-tabs: "Kracht oefeningen" (globale FitSmith DB) | "Fysio oefeningen" (eigen bibliotheek)
 */
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Plus, X, Edit2, Trash2, Search, Image, AlertCircle,
  CheckCircle, Dumbbell, Activity, ChevronDown, Upload, Youtube, Play, ExternalLink
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SPIERGROEPEN = [
  "Borst", "Rug", "Schouders", "Biceps", "Triceps",
  "Benen", "Billen", "Buik", "Core", "Cardio", "Mobiliteit", "Anders",
];

// ── YouTube helpers ────────────────────────────────────────────────────────────
function extractYouTubeId(url = "") {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return m ? m[1] : null;
}
function ytThumb(id) { return `https://img.youtube.com/vi/${id}/mqdefault.jpg`; }
function ytEmbed(id)  { return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`; }

// ── Video afspeel modal ────────────────────────────────────────────────────────
function VideoModal({ videoId, naam, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-white font-semibold truncate pr-4">{naam}</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-white shrink-0"><X size={20} /></button>
        </div>
        <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingTop: "56.25%" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={ytEmbed(videoId)}
            title={naam}
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

// ── Toast helper ──────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold shadow-2xl ${
      type === "error" ? "bg-red-900/90 text-red-100 border border-red-500/30" : "bg-[#0f0f0f] text-white border border-[#C9A84C]/30"
    }`}>
      {type === "error" ? <AlertCircle size={15} /> : <CheckCircle size={15} className="text-[#C9A84C]" />}
      {msg}
    </div>
  );
}

// ── Afbeelding resizer (base64) ───────────────────────────────────────────────
function resizeImage(file, maxW = 800, maxH = 600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Bestand lezen mislukt"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Afbeelding laden mislukt"));
      img.onload = () => {
        let { width: w, height: h } = img;
        if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
        if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ── Lege oefening form ────────────────────────────────────────────────────────
const EMPTY = {
  naam: "", categorie: "fysio", spiergroep: "Anders", beschrijving: "",
  aandachtspunten: "", sets: "", herhalingen: "", rusttijd: "", tempo: "",
  uitvoeringstip: "", afbeelding_url: "", video_url: "",
};

// ── Oefening Aanmaken / Bewerken Modal ────────────────────────────────────────
function OefeningModal({ token, initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("Afbeelding te groot (max 5 MB)"); return; }
    setImgLoading(true);
    try {
      const b64 = await resizeImage(file);
      upd("afbeelding_url", b64);
    } catch { setErr("Afbeelding laden mislukt"); }
    finally { setImgLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.naam.trim()) { setErr("Naam is verplicht"); return; }
    setSaving(true);
    try {
      const isEdit = !!initial?.id;
      const url = isEdit
        ? `${API}/api/company/exercises/${initial.id}`
        : `${API}/api/company/exercises`;
      const r = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      const data = await r.json();
      onSave({ ...form, id: initial?.id || data.id });
    } catch (err) { setErr(err.message); }
    finally { setSaving(false); }
  };

  const FIELDS = [
    { key: "naam",            label: "Naam *",                  type: "text",     placeholder: "bijv. Knierekkend strekken", required: true },
    { key: "beschrijving",    label: "Beschrijving / uitleg",   type: "textarea", placeholder: "Beschrijf de uitvoering stap voor stap..." },
    { key: "aandachtspunten", label: "Aandachtspunten",         type: "textarea", placeholder: "bijv. Rug recht houden, geen pijn forceren..." },
    { key: "uitvoeringstip",  label: "Extra tip (optioneel)",   type: "text",     placeholder: "bijv. Adem uit bij de inspanning" },
  ];
  const PARAMS = [
    { key: "sets",        label: "Aantal sets",         placeholder: "bijv. 3" },
    { key: "herhalingen", label: "Herhalingen / tijd",  placeholder: "bijv. 10-12 of 30 sec" },
    { key: "rusttijd",    label: "Rusttijd",            placeholder: "bijv. 60 sec" },
    { key: "tempo",       label: "Tempo (optioneel)",   placeholder: "bijv. 3-1-3" },
  ];

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-[#111] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 flex-shrink-0">
          <div>
            <h3 className="text-white font-bold">{initial?.id ? "Oefening bewerken" : "Nieuwe oefening"}</h3>
            <p className="text-zinc-500 text-xs mt-0.5">Wordt opgeslagen in jouw eigen bibliotheek</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Categorie toggle */}
          <div>
            <label className="block text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-2">Categorie</label>
            <div className="flex gap-2">
              {[
                { val: "fysio",  label: "Fysio oefening",  icon: <Activity size={13} /> },
                { val: "kracht", label: "Krachtoefening",  icon: <Dumbbell size={13} /> },
              ].map(({ val, label, icon }) => (
                <button type="button" key={val} onClick={() => upd("categorie", val)}
                  data-testid={`cat-${val}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                    form.categorie === val
                      ? "bg-[#C9A84C] text-black border-[#C9A84C]"
                      : "bg-[#1a1a1a] text-zinc-400 border-white/8 hover:border-white/20"
                  }`}>
                  {icon} {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tekstvelden */}
          {FIELDS.map(({ key, label, type, placeholder }) => (
            <div key={key}>
              <label className="block text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1.5">{label}</label>
              {type === "textarea" ? (
                <textarea value={form[key]} onChange={e => upd(key, e.target.value)}
                  placeholder={placeholder} rows={3}
                  className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm resize-none focus:outline-none focus:border-[#C9A84C]/40 placeholder-zinc-600"
                  data-testid={`field-${key}`} />
              ) : (
                <input type="text" value={form[key]} onChange={e => upd(key, e.target.value)}
                  placeholder={placeholder}
                  className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40 placeholder-zinc-600"
                  data-testid={`field-${key}`} />
              )}
            </div>
          ))}

          {/* Spiergroep */}
          <div>
            <label className="block text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1.5">Spiergroep / lichaamsdeel</label>
            <div className="relative">
              <select value={form.spiergroep} onChange={e => upd("spiergroep", e.target.value)}
                className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40 appearance-none"
                data-testid="field-spiergroep">
                {SPIERGROEPEN.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            </div>
          </div>

          {/* Parameters */}
          <div>
            <label className="block text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-2">Parameters</label>
            <div className="grid grid-cols-2 gap-2">
              {PARAMS.map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-zinc-600 text-[10px] mb-1">{label}</label>
                  <input type="text" value={form[key]} onChange={e => upd(key, e.target.value)}
                    placeholder={placeholder}
                    className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2 text-white text-xs focus:outline-none focus:border-[#C9A84C]/40 placeholder-zinc-700"
                    data-testid={`field-${key}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Afbeelding upload */}
          <div>
            <label className="block text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-2">Afbeelding (optioneel)</label>
            <div className="flex items-center gap-3">
              <div
                onClick={() => fileRef.current?.click()}
                className="w-20 h-20 rounded-xl border-2 border-dashed border-white/15 flex items-center justify-center cursor-pointer hover:border-[#C9A84C]/40 transition-colors overflow-hidden shrink-0 bg-[#0f0f0f]"
                data-testid="img-upload-preview">
                {imgLoading ? (
                  <div className="w-5 h-5 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
                ) : form.afbeelding_url ? (
                  <img src={form.afbeelding_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Upload size={20} className="text-zinc-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-zinc-400 text-xs mb-2">PNG of JPG · max 5 MB · wordt verkleind naar 800×600</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-white/10 text-zinc-300 hover:border-white/25">
                    {form.afbeelding_url ? "Wijzigen" : "Upload foto"}
                  </button>
                  {form.afbeelding_url && (
                    <button type="button" onClick={() => upd("afbeelding_url", "")}
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-500/20 text-red-400">
                      Verwijderen
                    </button>
                  )}
                </div>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
              onChange={handleImg} className="hidden" />
          </div>

          {/* YouTube video URL */}
          <div>
            <label className="block text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-2">
              YouTube video (optioneel)
            </label>
            <div className="relative">
              <Youtube size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="url"
                value={form.video_url}
                onChange={e => upd("video_url", e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                data-testid="field-video_url"
                className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl pl-8 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40 placeholder-zinc-600"
              />
            </div>
            {/* Thumbnail preview */}
            {extractYouTubeId(form.video_url) && (
              <div className="mt-2 flex items-center gap-3 p-2.5 bg-[#0f0f0f] border border-white/5 rounded-xl">
                <div className="relative shrink-0">
                  <img
                    src={ytThumb(extractYouTubeId(form.video_url))}
                    alt="YouTube thumbnail"
                    className="w-20 h-12 rounded-lg object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-red-600/90 flex items-center justify-center">
                      <Play size={10} className="text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="text-zinc-300 text-xs font-semibold">Video herkend</p>
                  <p className="text-zinc-600 text-[10px] truncate">ID: {extractYouTubeId(form.video_url)}</p>
                </div>
                <button type="button" onClick={() => upd("video_url", "")}
                  className="ml-auto text-zinc-600 hover:text-red-400 transition-colors shrink-0">
                  <X size={13} />
                </button>
              </div>
            )}
          </div>

          {err && <p className="text-red-400 text-sm">{err}</p>}
        </form>

        <div className="p-5 border-t border-white/5 flex-shrink-0 flex gap-3">
          <button type="button" onClick={onClose}
            className="flex-1 py-3 rounded-xl text-sm font-bold border border-white/8 text-zinc-400 hover:text-white">
            Annuleren
          </button>
          <button onClick={handleSubmit} disabled={saving}
            data-testid="save-oefening-btn"
            className="flex-1 py-3 rounded-xl text-black font-bold text-sm disabled:opacity-50 bg-[#C9A84C]">
            {saving ? "Opslaan..." : (initial?.id ? "Wijzigingen opslaan" : "Oefening aanmaken")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Fysio Oefeningen CRUD ──────────────────────────────────────────────────────
function FysioTab({ token }) {
  const [oefeningen, setOefeningen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null); // { videoId, naam }

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/company/exercises/bibliotheek`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setOefeningen(d.exercises || []);
      }
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = oefeningen.filter(o =>
    o.categorie === "fysio" &&
    (!query || o.naam?.toLowerCase().includes(query.toLowerCase()) ||
     o.spiergroep?.toLowerCase().includes(query.toLowerCase()))
  );

  const handleSave = async (saved) => {
    await load();
    setModalOpen(false);
    setEditing(null);
    showToast(editing ? "Oefening bijgewerkt!" : "Oefening aangemaakt!");
  };

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API}/api/company/exercises/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      setOefeningen(p => p.filter(o => o.id !== id));
      showToast("Oefening verwijderd");
    } catch { showToast("Verwijderen mislukt", "error"); }
    setConfirmDel(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-white font-bold">Fysio oefeningen</h3>
          <p className="text-zinc-500 text-sm mt-0.5">Jouw eigen fysiotherapie-bibliotheek ({filtered.length} oefeningen)</p>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true); }}
          data-testid="add-fysio-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-black font-bold text-sm bg-[#C9A84C] hover:opacity-90 transition-opacity">
          <Plus size={15} /> Nieuwe oefening
        </button>
      </div>

      {/* Zoekbalk */}
      <div className="flex items-center gap-2 bg-[#0f0f0f] border border-white/5 rounded-xl px-3 py-2.5 mb-5">
        <Search size={14} className="text-zinc-500 shrink-0" />
        <input value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Zoek op naam of lichaamsdeel..."
          className="bg-transparent flex-1 text-sm text-white outline-none placeholder-zinc-600"
          data-testid="search-fysio" />
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-600 text-sm">Laden...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-[#0f0f0f] border border-white/5 flex items-center justify-center mx-auto mb-4">
            <Activity size={24} className="text-zinc-600" />
          </div>
          <p className="text-white font-bold mb-1">Nog geen fysio oefeningen</p>
          <p className="text-zinc-500 text-sm mb-4">Voeg jouw eerste revalidatie-oefening toe aan de bibliotheek</p>
          <button onClick={() => { setEditing(null); setModalOpen(true); }}
            className="px-5 py-2.5 rounded-xl text-black font-bold text-sm bg-[#C9A84C]">
            Eerste oefening toevoegen
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => (
            <div key={o.id} className="bg-[#0f0f0f] border border-white/5 rounded-2xl overflow-hidden">
              <div className="flex items-start gap-4 p-4">
                {/* Afbeelding */}
                {o.afbeelding_url ? (
                  <img src={o.afbeelding_url} alt={o.naam}
                    className="w-16 h-16 rounded-xl object-cover shrink-0 border border-white/8" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-[#1a1a1a] border border-white/5 flex items-center justify-center shrink-0">
                    <Image size={20} className="text-zinc-600" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-white font-bold text-sm">{o.naam}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-zinc-500 text-xs">{o.spiergroep}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-[#C9A84C]/10 text-[#C9A84C]">Fysio</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => { setEditing(o); setModalOpen(true); }}
                        data-testid={`edit-oefening-${o.id}`}
                        className="p-1.5 rounded-lg text-zinc-500 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors">
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => setConfirmDel(o.id)}
                        data-testid={`del-oefening-${o.id}`}
                        className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {o.beschrijving && (
                    <p className="text-zinc-400 text-xs mt-2 line-clamp-2">{o.beschrijving}</p>
                  )}

                  {/* Parameters */}
                  {(o.sets || o.herhalingen || o.rusttijd || o.tempo) && (
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {o.sets && <span className="text-xs text-zinc-500"><span className="text-white font-semibold">{o.sets}</span> sets</span>}
                      {o.herhalingen && <span className="text-xs text-zinc-500"><span className="text-white font-semibold">{o.herhalingen}</span> reps</span>}
                      {o.rusttijd && <span className="text-xs text-zinc-500">Rust: <span className="text-white font-semibold">{o.rusttijd}</span></span>}
                      {o.tempo && <span className="text-xs text-zinc-500">Tempo: <span className="text-white font-semibold">{o.tempo}</span></span>}
                    </div>
                  )}

                  {o.aandachtspunten && (
                    <div className="mt-2 p-2.5 rounded-lg bg-[#C9A84C]/5 border border-[#C9A84C]/10">
                      <p className="text-[10px] font-bold text-[#C9A84C] uppercase tracking-wide mb-0.5">Aandachtspunten</p>
                      <p className="text-zinc-400 text-xs line-clamp-2">{o.aandachtspunten}</p>
                    </div>
                  )}

                  {/* YouTube video indicator */}
                  {extractYouTubeId(o.video_url) && (
                    <button
                      onClick={() => setPlayingVideo({ videoId: extractYouTubeId(o.video_url), naam: o.naam })}
                      data-testid={`play-video-${o.id}`}
                      className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded-xl border border-red-500/20 bg-red-600/8 text-red-400 hover:bg-red-600/15 transition-colors text-xs font-semibold w-fit">
                      <div className="w-4 h-4 rounded-full bg-red-600 flex items-center justify-center shrink-0">
                        <Play size={7} fill="white" className="text-white ml-[1px]" />
                      </div>
                      Instructievideo beschikbaar
                    </button>
                  )}
                </div>
              </div>

              {/* Verwijder bevestiging */}
              {confirmDel === o.id && (
                <div className="border-t border-white/5 bg-red-950/20 px-4 py-3 flex items-center justify-between">
                  <p className="text-red-300 text-xs">Weet je zeker dat je "<strong>{o.naam}</strong>" wil verwijderen?</p>
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmDel(null)} className="px-3 py-1.5 text-xs rounded-lg border border-white/10 text-zinc-400">Annuleren</button>
                    <button onClick={() => handleDelete(o.id)}
                      data-testid={`confirm-del-${o.id}`}
                      className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white font-bold">Verwijderen</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <OefeningModal
          token={token}
          initial={editing ? { ...editing, id: editing.id } : null}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditing(null); }}
        />
      )}

      {playingVideo && (
        <VideoModal
          videoId={playingVideo.videoId}
          naam={playingVideo.naam}
          onClose={() => setPlayingVideo(null)}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ── Kracht Oefeningen (globale FitSmith database) ─────────────────────────────
function KrachtTab({ token }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [spierFilter, setSpierFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/company/exercises`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setExercises((d.exercises || []).filter(e => !e.company_only || e.categorie === "kracht"));
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = exercises.filter(e =>
    (!query || e.naam.toLowerCase().includes(query.toLowerCase())) &&
    (!spierFilter || e.spiergroep === spierFilter)
  );

  return (
    <div>
      <div className="mb-5">
        <h3 className="text-white font-bold">Kracht oefeningen</h3>
        <p className="text-zinc-500 text-sm mt-0.5">Volledige FitSmith oefenendatabase ({exercises.length} oefeningen) — eigen oefeningen zijn bewerkbaar</p>
      </div>

      <div className="flex items-center gap-2 bg-[#0f0f0f] border border-white/5 rounded-xl px-3 py-2.5 mb-3">
        <Search size={14} className="text-zinc-500 shrink-0" />
        <input value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Zoek krachtoefening..."
          className="bg-transparent flex-1 text-sm text-white outline-none placeholder-zinc-600"
          data-testid="search-kracht" />
      </div>

      <div className="flex gap-1.5 flex-wrap mb-5">
        <button onClick={() => setSpierFilter("")}
          className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${!spierFilter ? "bg-[#C9A84C] text-black" : "bg-[#0f0f0f] border border-white/5 text-zinc-400 hover:text-white"}`}>
          Alles
        </button>
        {SPIERGROEPEN.map(s => (
          <button key={s} onClick={() => setSpierFilter(spierFilter === s ? "" : s)}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${spierFilter === s ? "bg-[#C9A84C] text-black" : "bg-[#0f0f0f] border border-white/5 text-zinc-400 hover:text-white"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-zinc-500 text-sm text-center py-8">Laden...</p>
      ) : (
        <div className="grid gap-1.5">
          {filtered.map((ex, i) => {
            const videoId = extractYouTubeId(ex.video_url);
            return (
              <div key={i} className="flex items-center justify-between px-4 py-3 bg-[#0f0f0f] border border-white/5 rounded-xl group">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{ex.naam}</p>
                  {(ex.type || ex.apparaat) && (
                    <p className="text-zinc-600 text-xs">{[ex.type, ex.apparaat].filter(Boolean).join(" · ")}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  {videoId && (
                    <button
                      onClick={() => setPlayingVideo({ videoId, naam: ex.naam })}
                      data-testid={`play-kracht-video-${i}`}
                      className="p-1.5 rounded-lg bg-red-600/10 text-red-400 hover:bg-red-600/20 transition-colors">
                      <Play size={12} fill="currentColor" />
                    </button>
                  )}
                  {ex.company_only && (
                    <button
                      onClick={() => setEditing(ex)}
                      data-testid={`edit-kracht-${i}`}
                      className="p-1.5 rounded-lg text-zinc-600 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors opacity-0 group-hover:opacity-100">
                      <Edit2 size={12} />
                    </button>
                  )}
                  {ex.company_only && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-[#C9A84C]/10 text-[#C9A84C]">eigen</span>}
                  <span className="text-zinc-600 text-xs">{ex.spiergroep}</span>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">Geen oefeningen gevonden</p>}
        </div>
      )}

      {/* Edit modal voor eigen krachtoefeningen */}
      {editing && (
        <OefeningModal
          token={token}
          initial={{ ...editing, categorie: "kracht" }}
          onSave={async () => { await load(); setEditing(null); setToast({ msg: "Oefening bijgewerkt!", type: "success" }); }}
          onClose={() => setEditing(null)}
        />
      )}

      {playingVideo && (
        <VideoModal
          videoId={playingVideo.videoId}
          naam={playingVideo.naam}
          onClose={() => setPlayingVideo(null)}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ── BiblioteekTab (hoofd-component) ──────────────────────────────────────────
export default function BiblioteekTab({ token }) {
  const [subTab, setSubTab] = useState("fysio");

  return (
    <div>
      <div className="mb-6">
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Oefeningen</p>
        <h2 className="text-xl font-bold text-white">Oefeningenbibliotheek</h2>
        <p className="text-zinc-500 text-sm mt-1">Beheer jouw eigen fysio-oefeningen en bekijk de volledige FitSmith database.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-[#0f0f0f] border border-white/5 rounded-xl p-1 w-fit mb-8">
        {[
          { key: "fysio",  label: "Fysio oefeningen", icon: <Activity size={14} /> },
          { key: "kracht", label: "Kracht oefeningen", icon: <Dumbbell size={14} /> },
        ].map(({ key, label, icon }) => (
          <button key={key} onClick={() => setSubTab(key)}
            data-testid={`subtab-${key}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              subTab === key ? "bg-[#C9A84C] text-black" : "text-zinc-500 hover:text-white"
            }`}>
            {icon} {label}
          </button>
        ))}
      </div>

      {subTab === "fysio"  && <FysioTab  token={token} />}
      {subTab === "kracht" && <KrachtTab token={token} />}
    </div>
  );
}
